// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what the search surface decides
//  ---------------------------------------------------------------------------
//  The palette (md-find.jsx) is deliberately thin: a field, a list, a
//  keystroke. Everything it DECIDES lives here, as pure functions over plain
//  data — the same split md-search.ts makes for the engine's half, for the same
//  reason: the decisions can be proven in tests/find.test.js without a browser,
//  a worker, or a render.
//
//  THE ENGINE IS INCLUSIVE, THE SURFACE IS SELECTIVE. C3 indexes every family
//  so nothing is ever unfindable — including families nobody has invented yet.
//  But a result in the palette is a promise: choose it and you are TAKEN THERE.
//  mdFindPlace is the one list of places this app can actually take a person,
//  and a key it does not know is not shown rather than shown dead. When a new
//  surface earns a door, its family is one line here.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* ── the key that opens it ────────────────────────────────────────────────
     ⌘K, or Ctrl+K off a Mac. No other fingers: shift and alt chords belong to
     other owners (the browser, the OS, the board). Registered the way ⌘Z is in
     md-history.ts — one listener, defaultPrevented respected — but unlike ⌘Z it
     fires from inside a caret too: a search you cannot open while writing is a
     search you will not use, and opening it takes nothing from the text. */
  function mdFindAsks(event: any): boolean {
    if (!event || (!event.metaKey && !event.ctrlKey)) return false;
    if (event.altKey || event.shiftKey) return false;
    if (event.metaKey && event.ctrlKey) return false;
    return typeof event.key === 'string' && event.key.toLowerCase() === 'k';
  }

  /* ── where a result takes you ─────────────────────────────────────────────
     A storage key names a family, and a family either has a door or it does
     not. `null` means "indexed, findable one day, but not a place" — a view
     document, an org schema — and the palette leaves it out. */
  const DAY = /^\d{4}-\d{2}-\d{2}$/;
  const ID = /^[a-z0-9]{4,40}$/;

  function mdFindPlace(key: unknown): any {
    if (typeof key !== 'string' || !key.startsWith('md:')) return null;
    const parts = key.slice(3).split(':');
    const family = parts[0];
    if ((family === 'events' || family === 'records') && parts.length === 2 && DAY.test(parts[1]!)) {
      return { go: 'day', day: parts[1] };
    }
    if (family === 'project' && parts.length === 2 && ID.test(parts[1]!)) return { go: 'project', id: parts[1] };
    if (family === 'projects' && parts.length === 1) return { go: 'workshop' };
    if (family === 'pursuit' && parts.length === 2 && ID.test(parts[1]!)) return { go: 'pursuit', id: parts[1] };
    if (family === 'domain' && parts.length === 2 && /^e\d{1,3}$/.test(parts[1]!)) return { go: 'domain', id: parts[1] };
    if (family === 'database' && parts.length === 2 && ID.test(parts[1]!)) return { go: 'database', id: parts[1] };
    if (family === 'database-row' && parts.length === 3 && ID.test(parts[1]!) && ID.test(parts[2]!)) {
      return { go: 'database', id: parts[1], row: parts[2] };
    }
    if (family === 'view' && parts.length === 2 && ID.test(parts[1]!)) {
      const value = typeof g.mdRead === 'function' ? g.mdRead(key, null) : null;
      const doc = typeof g.mdViewDocSanitise === 'function' ? g.mdViewDocSanitise(value, parts[1]) : value;
      if (doc && ID.test(doc.databaseId || '')) {
        return { go: 'view', scope: 'database', databaseId: doc.databaseId, id: parts[1] };
      }
    }
    if (family === 'board' && parts.length === 2 && ID.test(parts[1]!)) return { go: 'board', id: parts[1] };
    /* One Crafts space holds them all; the release id is not addressable
       inside it yet, so the door is the space rather than a pretence of depth. */
    if (family === 'release' && parts.length === 2 && ID.test(parts[1]!)) return { go: 'crafts', id: parts[1] };
    return null;
  }

  /* ── a day becomes the offset the app steers by ───────────────────────────
     viewOffset is days-from-today, and this is the one conversion. UTC on both
     sides so a DST boundary between here and there cannot make a day 23 hours
     long and the division land on a fraction. */
  function mdFindDayOffset(day: unknown, today: unknown): number | null {
    if (typeof day !== 'string' || typeof today !== 'string' || !DAY.test(day) || !DAY.test(today)) return null;
    const at = Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
    const now = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
    return Math.round((at - now) / 86400000);
  }

  /* ── a stale answer never wins ────────────────────────────────────────────
     The worker may answer out of order and typing outruns querying. Every ask
     takes a ticket; only the newest ticket's answer is still standing when it
     lands, and the rest are dropped without rendering. */
  function mdFindTicket() {
    let latest = 0;
    return {
      ask: () => ++latest,
      standing: (seq: number) => seq === latest,
    };
  }

  /* ── the matched words, marked ────────────────────────────────────────────
     The engine wraps every hit in MD_SEARCH_MARK's pair of private-use
     characters — chosen because no keyboard on earth types them, so they can
     never collide with somebody's own writing. This turns the wrapped string
     into segments a renderer can set alight, and strips the marks whatever
     happens: a control character shown to a person is worse than no
     highlight. */
  const MARKS = () => (Array.isArray(g.MD_SEARCH_MARK) ? g.MD_SEARCH_MARK : ['\uE000', '\uE001']);

  function mdFindMarks(snippet: unknown): Array<{ text: string; hit: boolean }> {
    if (typeof snippet !== 'string' || !snippet) return [];
    const [open, close] = MARKS();
    const out: Array<{ text: string; hit: boolean }> = [];
    let lit = false;
    for (const piece of snippet.split(open)) {
      const shut = piece.indexOf(close);
      if (shut === -1) {
        if (piece) out.push({ text: piece, hit: false });
        continue;
      }
      const hit = piece.slice(0, shut);
      const rest = piece.slice(shut + close.length);
      if (hit) out.push({ text: hit, hit: true });
      if (rest) out.push({ text: rest, hit: false });
      void lit;
    }
    return out;
  }

  Object.assign(g, { mdFindAsks, mdFindPlace, mdFindDayOffset, mdFindTicket, mdFindMarks });
})(typeof window !== 'undefined' ? window : globalThis);
